// ============================================================
// ELC Shift — Uploads / Logs module (v104.0)
//
// Log templates admin: define + manage log templates (
// daily/weekly safety, etc.) that drive the staff-facing log
// entry UI in index.html.
//
// Public surface:
//   • loadLogTemplatesAdmin
//   • renderLogsAdminEntries
//   • saveLogTemplate
//   • toggleLogTemplateActive
//   • deleteLogTemplate
//   • renderLogTemplateList
//   • renderLogTemplateFieldsEditor
//
// Free globals consumed:
//   DB, showToast, escapeHtml, formatDateShort, ALL_STORES
// ============================================================

export async function loadLogTemplatesAdmin() {
  try {
    const rows = await DB.logTemplates.list();
    logTemplatesAdmin = Array.isArray(rows) ? rows : [];
  } catch(e) {
    console.warn('[DB] Log templates load failed:', e.message);
    logTemplatesAdmin = [];
  }
  // v55: idempotent seed of the Top 10 Stock Check log type (called out from
  // the weekly Safety & Compliance checklist). Only admin role seeds.
  try {
    if (currentAdmin && currentAdmin.role === 'admin') {
      await seedTop10StockLogIfMissing();
    }
  } catch(e) { console.warn('[seed] top-10 stock log:', e.message); }
}

export async function renderLogsAdminEntries() {
  const tbody = document.getElementById('logsAdminTbody');
  const thead = document.getElementById('logsAdminThead');
  const logType = document.getElementById('logsAdminTypeFilter').value;
  const store = document.getElementById('logsAdminStoreFilter').value;
  const from = document.getElementById('logsAdminFrom').value;
  const to = document.getElementById('logsAdminTo').value;

  if (!logType) {
    thead.innerHTML = '';
    tbody.innerHTML = '<tr><td style="text-align:center;padding:24px;color:var(--sub)">No log types set up yet. Go to "Manage Log Types" to add one.</td></tr>';
    return;
  }

  const tmpl = logTemplatesAdmin.find(t => t.type === logType);
  if (!tmpl) {
    thead.innerHTML = '';
    tbody.innerHTML = '<tr><td style="text-align:center;padding:24px;color:var(--sub)">Template not found.</td></tr>';
    return;
  }

  // Build header row from template fields
  const fields = Array.isArray(tmpl.fields) ? tmpl.fields : [];
  let headCells = '<th>Date</th><th>Time</th><th>Store</th><th>Staff</th>';
  fields.forEach(f => { headCells += '<th translate="no">' + escapeHtml(f.label) + '</th>'; });
  headCells += '<th>Notes</th>';
  thead.innerHTML = '<tr>' + headCells + '</tr>';

  tbody.innerHTML = '<tr><td colspan="' + (4 + fields.length + 1) + '" style="text-align:center;padding:24px;color:var(--sub)">Loading…</td></tr>';

  try {
    const params = { logType };
    if (store) params.store = store;
    if (from)  params.dateFrom = from;
    if (to)    params.dateTo = to;
    const rows = await DB.logs.list(params);
    logEntriesAdmin = Array.isArray(rows) ? rows : [];
  } catch(e) {
    console.warn('[DB] Log entries load failed:', e.message);
    tbody.innerHTML = '<tr><td colspan="' + (4 + fields.length + 1) + '" style="text-align:center;padding:24px;color:var(--danger)">Failed to load: ' + escapeHtml(e.message||'') + '</td></tr>';
    return;
  }

  if (!logEntriesAdmin.length) {
    tbody.innerHTML = '<tr><td colspan="' + (4 + fields.length + 1) + '" style="text-align:center;padding:24px;color:var(--sub)">No entries match these filters.</td></tr>';
    return;
  }

  const rowsHtml = logEntriesAdmin.map(e => {
    const storeShort = (e.store || '').split(' - ').pop();
    const timeStr = e.entry_time ? String(e.entry_time).slice(0,5) : '—';
    const staffName = e.staff_name || e.created_by || '—';
    const data = e.data || {};
    let cells = '<td style="font-size:11px">' + escapeHtml(e.entry_date || '') + '</td>';
    cells += '<td style="font-size:11px">' + escapeHtml(timeStr) + '</td>';
    cells += '<td style="font-size:11px" translate="no">' + escapeHtml(storeShort) + '</td>';
    cells += '<td style="font-weight:600" translate="no">' + escapeHtml(staffName) + '</td>';
    fields.forEach(f => {
      const v = data[f.key];
      cells += '<td style="font-size:12px" translate="no">' + escapeHtml(v == null ? '—' : String(v)) + '</td>';
    });
    cells += '<td style="font-size:11px;color:var(--sub);max-width:200px" translate="no">' + escapeHtml(e.notes || '') + '</td>';
    return '<tr>' + cells + '</tr>';
  }).join('');
  tbody.innerHTML = rowsHtml;
}

export async function saveLogTemplate() {
  if (!logTplEditing) return;
  const name = document.getElementById('logTplName').value.trim();
  if (!name) { showToast('Please give the log type a name', 'error'); return; }
  const icon = document.getElementById('logTplIcon').value.trim() || '📝';
  const description = document.getElementById('logTplDescription').value.trim();
  const active = document.getElementById('logTplActive').checked;
  // D1 — "required for compliance" (default true). Off = never flagged as missed.
  const requiredEl = document.getElementById('logTplRequired');
  const required = requiredEl ? requiredEl.checked : true;
  // v125.31 — daily (default) or weekly. Weekly shows on Mondays on the staff
  // Checks screen; the compliance engine expects one entry per week.
  const freqEl = document.getElementById('logTplFrequency');
  const frequency = (freqEl && freqEl.value === 'weekly') ? 'weekly' : 'daily';

  // Validate fields and auto-derive keys from labels if missing
  const cleanFields = (logTplEditing.fields || []).map(f => {
    const label = (f.label || '').trim();
    let key = (f.key || '').trim();
    if (!key && label) {
      key = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    }
    return {
      label, key,
      type: f.type || 'text',
      required: !!f.required,
    };
  }).filter(f => f.label && f.key);

  // Check for duplicate keys
  const seen = {};
  for (const f of cleanFields) {
    if (seen[f.key]) { showToast('Duplicate field key: ' + f.key, 'error'); return; }
    seen[f.key] = true;
  }

  const payload = {
    type: logTplEditing.type,
    name, icon, description,
    fields: cleanFields,
    active,
    required,
    frequency,
    sort_order: logTplEditing.sort_order || 0,
  };

  try {
    await DB.logTemplates.save(payload);
    showToast('"' + name + '" saved ✓', 'success');
    logTplEditing = null;
    document.getElementById('logTemplateEditorCard').style.display = 'none';
    await loadLogTemplatesAdmin();
    renderLogTemplateList();
    // Also refresh the type filter dropdown in the Entries tab
    const typeSel = document.getElementById('logsAdminTypeFilter');
    if (typeSel) {
      const cur = typeSel.value;
      typeSel.innerHTML = '';
      logTemplatesAdmin.forEach(t => {
        const o = document.createElement('option');
        o.value = t.type; o.textContent = (t.icon || '📝') + ' ' + t.name;
        typeSel.appendChild(o);
      });
      if (cur) typeSel.value = cur;
    }
  } catch(e) {
    console.error('[DB] Template save failed:', e);
    showToast('Save failed: ' + (e.message || 'unknown'), 'error');
  }
}

export async function toggleLogTemplateActive(type) {
  const t = logTemplatesAdmin.find(x => x.type === type);
  if (!t) return;
  const newActive = !(t.active !== false);
  try {
    await DB.logTemplates.update(type, { active: newActive });
    showToast(t.name + ' ' + (newActive ? 'enabled' : 'disabled'), 'success');
    await loadLogTemplatesAdmin();
    renderLogTemplateList();
  } catch(e) {
    showToast('Update failed: ' + (e.message || 'unknown'), 'error');
  }
}

export async function deleteLogTemplate(type) {
  const t = logTemplatesAdmin.find(x => x.type === type);
  if (!t) return;
  if (!(await (window.uiConfirm || function (m) { return Promise.resolve(confirm(m)); })('Delete log type "' + t.name + '"? This cannot be undone. Existing entries of this type will remain in the database but may not render correctly.', { danger: true, confirmLabel: 'Delete' }))) return;
  try {
    await DB.logTemplates.remove(type);
    showToast(t.name + ' deleted', 'success');
    await loadLogTemplatesAdmin();
    renderLogTemplateList();
  } catch(e) {
    showToast('Delete failed: ' + (e.message || 'unknown'), 'error');
  }
}

export function renderLogTemplateList() {
  const list = document.getElementById('logTemplateList');
  if (!list) return;
  list.innerHTML = '';

  if (!logTemplatesAdmin.length) {
    list.innerHTML = '<div class="card" style="text-align:center;padding:24px;color:var(--sub)">No log types yet. Click "+ New Log Type" to create one.</div>';
    return;
  }

  const sorted = logTemplatesAdmin.slice().sort((a,b) => {
    const oa = a.sort_order || 0, ob = b.sort_order || 0;
    if (oa !== ob) return oa - ob;
    return (a.name || '').localeCompare(b.name || '');
  });

  sorted.forEach(t => {
    const fields = Array.isArray(t.fields) ? t.fields : [];
    const active = t.active !== false;
    const card = document.createElement('div');
    card.className = 'card';
    card.style.cssText = 'display:flex;justify-content:space-between;align-items:center;gap:12px;margin-bottom:8px;padding:14px;flex-wrap:wrap';
    card.innerHTML = `
      <div style="flex:1;min-width:200px">
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
          <strong style="font-size:14px" translate="no">${escapeHtml((t.icon || '📝') + ' ' + (t.name || t.type))}</strong>
          ${active
            ? '<span style="font-size:10px;background:var(--ok-bg);color:var(--ok-deep);padding:2px 8px;border-radius:6px;font-weight:700">ACTIVE</span>'
            : '<span style="font-size:10px;background:var(--danger-bg);color:var(--danger-deep);padding:2px 8px;border-radius:6px;font-weight:700">DISABLED</span>'
          }
        </div>
        <div style="font-size:12px;color:var(--sub);margin-top:4px">
          ${fields.length} custom field${fields.length === 1 ? '' : 's'}${t.description ? ' <span translate="no">· ' + escapeHtml(t.description) + '</span>' : ''}
        </div>
      </div>
      <div style="display:flex;gap:6px;flex-wrap:wrap">
        <button class="btn btn-outline btn-sm" onclick="editLogTemplate('${escapeAttr(t.type)}')">Edit</button>
        <button class="btn btn-outline btn-sm" onclick="Uploads.logs.toggleLogTemplateActive('${escapeAttr(t.type)}')">${active ? 'Disable' : 'Enable'}</button>
        <button class="btn btn-outline btn-sm" style="color:var(--danger);border-color:var(--adm-danger-line)" onclick="Uploads.logs.deleteLogTemplate('${escapeAttr(t.type)}')">Delete</button>
      </div>
    `;
    list.appendChild(card);
  });
}

export function renderLogTemplateFieldsEditor() {
  if (!logTplEditing) return;
  const ed = document.getElementById('logTplFieldsEditor');
  if (!ed) return;   // v136.8: null-safe (the box may not be on screen)
  ed.innerHTML = '';
  (logTplEditing.fields || []).forEach((f, i) => {
    const row = document.createElement('div');
    row.style.cssText = 'background:var(--surface-2);border-radius:10px;padding:10px;margin-bottom:8px';
    row.innerHTML = `
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr auto;gap:8px;align-items:end">
        <div>
          <label style="font-size:10px;font-weight:700;color:var(--sub);text-transform:uppercase">Label</label>
          <input type="text" value="${escapeAttr(f.label || '')}" data-fidx="${i}" data-fprop="label" class="filter-input lt-field-inp" style="width:100%">
        </div>
        <div>
          <label style="font-size:10px;font-weight:700;color:var(--sub);text-transform:uppercase">Key (no spaces)</label>
          <input type="text" value="${escapeAttr(f.key || '')}" data-fidx="${i}" data-fprop="key" class="filter-input lt-field-inp" style="width:100%">
        </div>
        <div>
          <label style="font-size:10px;font-weight:700;color:var(--sub);text-transform:uppercase">Type</label>
          <select data-fidx="${i}" data-fprop="type" class="filter-input lt-field-inp" style="width:100%">
            ${['text','number','date','textarea','yesno'].map(t =>
              `<option value="${t}"${(f.type||'text')===t?' selected':''}>${t}</option>`
            ).join('')}
          </select>
        </div>
        <button class="btn btn-outline btn-sm" style="color:var(--danger);border-color:var(--adm-danger-line)" onclick="removeLogTemplateField(${i})">×</button>
      </div>
      <div style="margin-top:6px">
        <label style="display:flex;align-items:center;gap:6px;font-size:12px;cursor:pointer">
          <input type="checkbox" ${f.required?'checked':''} data-fidx="${i}" data-fprop="required" class="lt-field-inp"> Required
        </label>
      </div>
    `;
    ed.appendChild(row);
  });

  // Bind inputs
  ed.querySelectorAll('.lt-field-inp').forEach(el => {
    el.addEventListener(el.type === 'checkbox' ? 'change' : 'input', (e) => {
      const idx = parseInt(el.dataset.fidx, 10);
      const prop = el.dataset.fprop;
      if (!logTplEditing.fields[idx]) return;
      if (el.type === 'checkbox') logTplEditing.fields[idx][prop] = el.checked;
      else logTplEditing.fields[idx][prop] = el.value;
    });
  });
}

